import type { ListDealWithRbo, UsePackageUsedByParam } from '@openx/types/package/PackageHookTypes';
import { useMemo } from 'react';
import { usePackageContext } from '../../utils/context/PackageContext';

export type UseRelatedDeals = (pckgUid: string | null) => {
  count: number;
  deals: ListDealWithRbo[];
  isWithRBOD: boolean;
  isLoading: boolean;
  hasInaccessibleDeals: boolean;
};

export const useRelatedDeals: UseRelatedDeals = pckgUid => {
  const { useDealsFetch, usePackageUsedBy } = usePackageContext();

  const { dealsLoading, deals } = useDealsFetch(pckgUid ?? '', {
    fields: 'name,deal_id,rbo_config,deal_participants',
    limit: 0,
    offset: 0,
    fetchAll: true,
    sort: 'name',
  });

  const packageUsedByParam: UsePackageUsedByParam = useMemo(() => {
    return {
      filters: { objects: ['deal', 'rbo'], package_uid: [pckgUid].filter(v => !!v) as string[] },
      networkOnly: true,
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: does not change for every render
  }, [pckgUid]);

  const { data: packageUsedByData, isLoading: packageUsedByLoading } = usePackageUsedBy(packageUsedByParam);

  const totalDealsReferenced = useMemo(() => {
    return (packageUsedByData ?? []).reduce((accumulator, currentValue) => {
      return accumulator + (currentValue.deals_referenced ?? 0);
    }, 0);
  }, [packageUsedByData]);

  const isWithRBOD = useMemo(
    () => packageUsedByData?.some(data => (data?.deals_with_rbo_dynamic || 0) > 0),
    [packageUsedByData]
  );

  return {
    isLoading: dealsLoading || packageUsedByLoading,
    isWithRBOD,
    count: totalDealsReferenced,
    deals: deals.objects ?? [],
    hasInaccessibleDeals: totalDealsReferenced > (deals.objects?.length ?? 0),
  };
};
